import DEFAULT_WA_ITEMS from "../constants/whatsapp-respuestas-default.json";

export const WA_LANGS = ["ES", "EN", "FR", "DE", "NL", "RU"];

/** Idiomas soportados para respuestas rápidas de WhatsApp (alineado con ficha cliente). */
export function normalizeWaIdioma(idioma) {
  const s = String(idioma || "es").trim().toLowerCase();
  const map = {
    es: "ES",
    en: "EN",
    fr: "FR",
    de: "DE",
    nl: "NL",
    ru: "RU",
  };
  return map[s] || "ES";
}

/** «1 video» / «N videos» (y equivalentes) según idioma. */
export function etiquetaVideos(n, idioma = "ES") {
  const v = Math.max(0, Number(n) || 0);
  if (v <= 0) return "";
  const lang = normalizeWaIdioma(idioma);
  switch (lang) {
    case "EN":
      return v === 1 ? "1 video" : `${v} videos`;
    case "FR":
      return v === 1 ? "1 vidéo" : `${v} vidéos`;
    case "DE":
      return v === 1 ? "1 Video" : `${v} Videos`;
    case "NL":
      return v === 1 ? "1 video" : `${v} video's`;
    case "RU":
      return v === 1 ? "1 видео" : `${v} видео`;
    default:
      return v === 1 ? "1 video" : `${v} videos`;
  }
}

/**
 * Inserta « y N videos» tras el tramo de propiedades de la frase.
 * Si no reconoce el patrón, añade al final.
 * Con vídeos (o plural de fichas) acuerda el verbo en ES: puede → pueden.
 */
export function anexarVideosAFrase(frase, videosCount, idioma = "ES") {
  const base = String(frase || "");
  const v = Math.max(0, Number(videosCount) || 0);
  if (!base || v <= 0) return base;
  const lang = normalizeWaIdioma(idioma);
  const vPart = etiquetaVideos(v, lang);
  const y =
    lang === "EN"
      ? " and "
      : lang === "FR"
        ? " et "
        : lang === "DE"
          ? " und "
          : lang === "NL"
            ? " en "
            : lang === "RU"
              ? " и "
              : " y ";

  const patterns = {
    ES: [
      [/\buna propiedad\b/i, `1 propiedad${y}${vPart}`],
      [/\b(\d+)\s+propiedades\b/i, `$1 propiedades${y}${vPart}`],
      [/\balgunas propiedades\b/i, `algunas propiedades${y}${vPart}`],
    ],
    EN: [
      [/\ba property\b/i, `1 property${y}${vPart}`],
      [/\b(\d+)\s+properties\b/i, `$1 properties${y}${vPart}`],
      [/\bsome properties\b/i, `some properties${y}${vPart}`],
    ],
    FR: [
      [/\bun bien\b/i, `1 bien${y}${vPart}`],
      [/\b(\d+)\s+biens\b/i, `$1 biens${y}${vPart}`],
      [/\bquelques biens\b/i, `quelques biens${y}${vPart}`],
    ],
    DE: [
      [/\beine Immobilie\b/i, `1 Immobilie${y}${vPart}`],
      [/\b(\d+)\s+Immobilien\b/i, `$1 Immobilien${y}${vPart}`],
      [/\beinige Immobilien\b/i, `einige Immobilien${y}${vPart}`],
    ],
    NL: [
      [/\been woning\b/i, `1 woning${y}${vPart}`],
      [/\b(\d+)\s+woningen\b/i, `$1 woningen${y}${vPart}`],
      [/\benkele woningen\b/i, `enkele woningen${y}${vPart}`],
    ],
    RU: [
      [/\bобъект(?!,)/i, `1 объект${y}${vPart}`],
      [/\b(\d+)\s+объектов\b/i, `$1 объектов${y}${vPart}`],
      [/\bнесколько объектов\b/i, `несколько объектов${y}${vPart}`],
    ],
  };

  const list = patterns[lang] || patterns.ES;
  let out = base;
  let matched = false;
  for (const [re, rep] of list) {
    if (re.test(out)) {
      out = out.replace(re, rep);
      matched = true;
      break;
    }
  }
  if (!matched) out = `${base}${y}${vPart}`;

  // Concordancia ES: 1 propiedad + videos → plural
  if (lang === "ES") {
    out = out
      .replace(/\bte puede interesar\b/gi, "te pueden interesar")
      .replace(/\bte puede gustar\b/gi, "te pueden gustar");
  }
  return out;
}

/** Échale → Échales cuando hay más de un adjunto (props y/o videos). */
export function acordarPluralPlantilla(text, propertiesCount, videosCount, idioma = "ES") {
  const t = String(text || "");
  const plural = (Number(propertiesCount) || 0) > 1 || (Number(videosCount) || 0) > 0;
  if (!plural || normalizeWaIdioma(idioma) !== "ES") return t;
  // Sin \b: «É» no es word-char en JS y \bÉchale no coincide.
  return t.replace(/Échale/g, "Échales").replace(/échale/g, "échales");
}

function fraseDesdePack(frases, count, videosCount = 0, idioma = "ES") {
  const n = Number(count) || 0;
  if (!frases || typeof frases !== "object") return "";
  let frase = "";
  if (n === 1) frase = String(frases.one || "");
  else if (n > 1) frase = String(frases.many || "").replace(/\{n\}/g, String(n));
  else frase = String(frases.zero || "");
  return anexarVideosAFrase(frase, videosCount, idioma);
}

/**
 * Construye respuestas rápidas desde ítems de BD (o fallback embebido).
 * @param {Array} items - filas whatsapp_respuestas_rapidas
 * @param {number} propertiesCount
 * @param {string} idioma
 * @param {number} [videosCount=0]
 */
export function buildWhatsAppQuickRepliesFromItems(
  items,
  propertiesCount = 0,
  idioma = "ES",
  videosCount = 0,
) {
  const lang = normalizeWaIdioma(idioma);
  const count = Math.max(0, Number(propertiesCount) || 0);
  const vids = Math.max(0, Number(videosCount) || 0);
  const sorted = [...(items || [])].sort(
    (a, b) => (a.orden || 0) - (b.orden || 0),
  );

  return sorted.map((item) => {
    const trad = item.traducciones || {};
    const tr = trad[lang] || trad.ES || {};
    const trEs = trad.ES || trad.es || {};
    const label = trEs.label || item.id;
    let text = "";
    if (item.uses_count) {
      const frase = fraseDesdePack(tr.frases, count, vids, lang);
      text = String(tr.template || "").replace(/\{frase\}/g, frase);
      text = acordarPluralPlantilla(text, count, vids, lang);
    } else {
      text = String(tr.template || "");
    }
    return {
      id: item.id,
      emoji: item.emoji || "💬",
      label,
      text,
    };
  });
}

/** Fallback local si la API no responde. */
export function buildWhatsAppQuickReplies(
  propertiesCount = 0,
  idioma = "ES",
  videosCount = 0,
) {
  return buildWhatsAppQuickRepliesFromItems(
    DEFAULT_WA_ITEMS,
    propertiesCount,
    idioma,
    videosCount,
  );
}

export { DEFAULT_WA_ITEMS };
